import React, { KeyboardEvent, useCallback } from "react";
import { X } from "lucide-react";
import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { formatNotificationTimestamp } from "config/dayjsConfig";
import type { Actor } from "types/actor";
import type { Notification, NotificationType } from "types/notification";
import { truncatePreview } from "util/previewText";
import {
  getNotificationUiStrings,
  resolveNotificationLocale,
  type NotificationUiStrings,
} from "i18n/notificationUi";

/**
 * Props consumed by the realtime notification preview toast. The
 * component is presentational: it does not know about routing, sockets,
 * or the modal controller. The container hook (`useRealtimeToasts`)
 * wires `onClick` to `useNotificationsModal().open()` and `onDismiss`
 * to `toast.dismiss(toastId)`.
 */
export interface NotificationPreviewToastProps {
  /** The full hydrated notification row from the `notification:new` event. */
  notification: Notification;
  /** Actor resolved into the canonical `Actor` shape used by `ActorAvatar`. */
  actor: Actor | null;
  /** Display name to use in the title. */
  actorDisplayName: string;
  /** Invoked when the user activates the toast (click, Enter, Space). */
  onClick: () => void;
  /** Invoked when the user dismisses the toast via the close button. */
  onDismiss: () => void;
  /**
   * Optional locale override for tests. When omitted, resolves from
   * `resolveNotificationLocale()` at render time.
   */
  localeStrings?: NotificationUiStrings;
}

const PREVIEW_MAX_GRAPHEMES = 140;

/**
 * Resolve a banner title family from a notification type. Keeps the
 * mapping declarative so a future notification kind can be slotted in
 * without changing component logic.
 */
const resolveTitleFamily = (
  notificationType: NotificationType,
  strings: NotificationUiStrings,
): string => {
  if (notificationType.startsWith("chat_")) {
    return strings.toastTitleChat;
  }
  if (notificationType.startsWith("system_")) {
    return strings.toastTitleAnnouncement;
  }
  if (
    notificationType.startsWith("vote_") ||
    notificationType.startsWith("comment_") ||
    notificationType.startsWith("follow_") ||
    notificationType.startsWith("subscription_") ||
    notificationType.startsWith("crosspost_") ||
    notificationType.startsWith("resteep_") ||
    notificationType.startsWith("teabate_argument_") ||
    notificationType === "content_award_received" ||
    notificationType === "share_perspective" ||
    notificationType === "coauthor_invite" ||
    notificationType === "coauthor_accept" ||
    notificationType === "team_join_request" ||
    notificationType === "team_invite" ||
    notificationType === "team_role_change" ||
    notificationType === "team_accept" ||
    notificationType === "action_join"
  ) {
    return strings.toastTitleSocial;
  }
  return strings.toastTitleSystem;
};

/**
 * Choose the most informative preview line from the notification's
 * `properties`, falling back to a localized generic line when no
 * preview is available. We do not render arbitrary HTML / markup; the
 * fields chosen here are produced server-side as plain text.
 */
const resolvePreviewLine = (
  notification: Notification,
  actorDisplayName: string,
  strings: NotificationUiStrings,
): string => {
  const props = notification.properties ?? {};

  if (typeof props.commentPreview === "string" && props.commentPreview.trim().length > 0) {
    return props.commentPreview;
  }
  if (typeof props.messagePreview === "string" && props.messagePreview.trim().length > 0) {
    return props.messagePreview;
  }
  if (typeof props.statement === "string" && props.statement.trim().length > 0) {
    return props.statement;
  }
  if (typeof props.actor_message === "string" && props.actor_message.trim().length > 0) {
    return props.actor_message;
  }
  if (typeof props.entityTitle === "string" && props.entityTitle.trim().length > 0) {
    return `${actorDisplayName}: ${props.entityTitle}`;
  }
  if (typeof notification.entity_name === "string" && notification.entity_name.trim().length > 0) {
    return `${actorDisplayName}: ${notification.entity_name}`;
  }
  return strings.toastDefaultPreview;
};

/**
 * Realtime in-app notification preview toast. Rendered inside the
 * dedicated top-center `<RealtimeToaster />` queue when a
 * `notification:new` event arrives. Clicking the toast opens the global
 * notifications modal via the shared `NotificationsModalContext`.
 *
 * Visual contract mirrors the chat toast for consistency:
 * - 40px actor avatar on the left
 * - Title row with banner family + relative timestamp
 * - One-line truncated preview using grapheme-aware slicing
 * - Close button on the far right
 *
 * Accessibility identical to `ChatPreviewToast`.
 */
const NotificationPreviewToast: React.FC<NotificationPreviewToastProps> = ({
  notification,
  actor,
  actorDisplayName,
  onClick,
  onDismiss,
  localeStrings,
}) => {
  const strings = localeStrings ?? getNotificationUiStrings(resolveNotificationLocale());

  const titleText = resolveTitleFamily(notification.notification_type, strings);
  const rawPreview = resolvePreviewLine(notification, actorDisplayName, strings);
  const previewLine = truncatePreview(rawPreview, PREVIEW_MAX_GRAPHEMES);
  const relativeTimestamp = notification.created_at
    ? formatNotificationTimestamp(notification.created_at)
    : "";

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const handleDismissClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();
      onDismiss();
    },
    [onDismiss],
  );

  return (
    <div
      className="flex w-full items-stretch overflow-hidden rounded-2xl bg-eoe-bg-modal shadow-xl ring-1 ring-black/5 dark:bg-eoe-dark-bg-modal dark:ring-white/10"
      data-testid="notification-preview-toast"
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={strings.toastAriaLabelOpen}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className="flex flex-1 items-start gap-3 px-3 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-eoe-purple cursor-pointer hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover transition-colors"
      >
        <div className="flex-shrink-0 pt-0.5">
          {actor ? (
            <ActorAvatar
              actor={actor}
              size={ACTOR_AVATAR_SIZES.message}
              clickTo="none"
              showRing={false}
            />
          ) : (
            <div className="flex size-8 items-center justify-center rounded-full bg-eoe-bg-hover text-xs font-semibold text-eoe-text dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text">
              {actorDisplayName.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
              {titleText}
            </span>
            {relativeTimestamp ? (
              <span className="flex-shrink-0 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {relativeTimestamp}
              </span>
            ) : null}
          </div>
          <span className="mt-0.5 line-clamp-2 break-words text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {previewLine}
          </span>
        </div>
      </div>
      <button
        type="button"
        aria-label={strings.toastAriaLabelDismiss}
        onClick={handleDismissClick}
        className="flex flex-shrink-0 items-start px-3 py-3 text-eoe-text-sub hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text outline-none focus-visible:ring-2 focus-visible:ring-eoe-purple"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default NotificationPreviewToast;
